/**
 * Shared transaction filters for the browser, node and edge Sentry configs.
 *
 * Kept in one module because a filter that is applied to only some runtimes silently does
 * nothing for the rest, and the transactions below are emitted from more than one.
 */

/** Our own Sentry DSN, shared by every sentry.*.config.js. */
export const SENTRY_DSN = 'https://35b3ef24671e822dcc587d23011649af@sentry-dev.inkitt.com/35';

/** Derived from the DSN so the two can't drift apart. */
export const SENTRY_INGEST_HOST = new URL(SENTRY_DSN).host;

/** True for requests to our own Sentry ingest endpoint. Relative URLs never are. */
export const isSentryIngestUrl = (url: string): boolean => {
  try {
    return new URL(url).host === SENTRY_INGEST_HOST;
  } catch {
    return false;
  }
};

export const IGNORED_TRANSACTIONS: RegExp[] = [
  /**
   * Sentry tracing its own envelope uploads, e.g.
   * `POST https://sentry-dev.inkitt.com/api/*​/envelope/`.
   *
   * This was the second highest-volume transaction in the project at P50 134 ms and User
   * Misery 0.8103 — high enough to visibly inflate project-wide latency and misery
   * aggregates with pure self-noise.
   *
   * Scoped to our own ingest host so another service's envelope-shaped path stays visible.
   * Anchored to the whole `METHOD https://host/path` name, so a longer host or our URL inside
   * another URL's path does not match.
   * The project id is matched as `[^/]+` rather than `\d+` on purpose: Sentry sanitises it
   * to a literal `*` in the transaction name, so a digits-only pattern never matches.
   */
  new RegExp(`^[A-Z]+ https?://${SENTRY_INGEST_HOST.replace(/\./g, '\\.')}/api/[^/]+/envelope/?$`),

  /**
   * Next.js middleware transactions, e.g. `middleware GET /*​/*​/*`.
   *
   * The single highest-volume transaction family in the project, measuring 10–15 ms of
   * pure middleware overhead under wildcard names that cannot be attributed back to a
   * route — so they cost quota without being usable for diagnosis. Errors thrown in
   * middleware are unaffected: those are reported as issues, not transactions.
   */
  /^middleware /,
];
